// src/lib/learn/path-progress.ts
//
// Where a student is on the learning path — pure.
//
// Takes the student's per-topic evidence (the same rows the dashboard Mastery
// Map and the Practice page read: getTopicMastery) plus the lessons they have
// marked read, and derives one state per step of src/lib/learn/learning-path.ts:
//
//   passed       the step's mastery tier meets its gate (Silver = ≥70% over ≥10),
//                OR the student passed a "Prove I know this" placement check on it
//                and has fewer than a skill check's worth of answers on the topic
//                (placement-check.ts — past ten answers the mastery tier decides)
//   in_progress  any evidence at all — an answered question or a read lesson —
//                short of the gate. Evidence beats the prerequisite graph: a
//                student already working a topic is never told it is locked.
//   locked       no evidence, and a prerequisite is not yet passed
//   available    no evidence, every prerequisite passed (or none to pass)
//
// …and the ONE step to continue with.
//   Personalised (Pro): the highest-leverage unlocked, unpassed step, ranked by
//   the same leverage engine the study plan uses — closing this gap is worth
//   the most points per question on the test.
//   Static (Free, or no evidence anywhere yet): the first in-progress step in
//   path order, else the first available one. For a brand-new student that is
//   Math step 1, Linear Equations in 1 Variable.
//
// PURE MODULE — client-safe. The db-backed loader is load-path-progress.ts.
// The output is plain JSON (no Map, no Set, no class instances) so it crosses
// the server-component boundary and the /api/learn/path wire unchanged.

import type { SatSectionCode } from '@/lib/sat/taxonomy'
import { rankTopicsByLeverage } from '@/lib/scoring/topic-leverage'
import type { WeakTopic } from '@/lib/study-plan/study-plan'
import { MASTERY_THRESHOLDS, masteryTier, type MasteryTier } from '@/lib/test-engine/sat-domains'
import { LEARNING_PATH, meetsGate, pathSteps } from './learning-path'
import { stepPlacement, type PlacementRecord, type StepPlacement } from './placement-check'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type PathStepState = 'locked' | 'available' | 'in_progress' | 'passed'

/** What a Passed step was passed on: its mastery tier, or a placement check. */
export type PathPassEvidence = 'mastery' | 'placement'

/** One topic's answer history, as getTopicMastery reports it. */
export type TopicEvidence = { topic: string; correct: number; total: number }

export type PathStepProgress = {
  topic: string
  lessonSlug: string
  section: SatSectionCode
  state: PathStepState
  tier: MasteryTier
  /** Rounded accuracy over the student's answers on the topic; null with none. */
  accuracyPct: number | null
  attempted: number
  /** The step's lesson, or one of its sub-topic modules, is marked read. */
  read: boolean
  /**
   * Prerequisites not yet passed. A step is locked only when this is non-empty
   * AND the student has no evidence on it; an in-progress step keeps its list
   * so the UI can still say what would normally come first.
   */
  missing: string[]
  /** Set when state is 'passed': the evidence behind it. Null otherwise. */
  passedBy: PathPassEvidence | null
  /** The "Prove I know this" placement check on this step (placement-check.ts). */
  placement: StepPlacement
}

export type PathNext = {
  topic: string
  lessonSlug: string
  /** Why this one: the best leverage (Pro), the step already underway, or the first open step. */
  reason: 'leverage' | 'in_progress' | 'first'
  /** The lesson is already read — the natural next click is the checkpoint, not the lesson. */
  read: boolean
}

export type PathTrackProgress = {
  section: SatSectionCode
  title: string
  passed: number
  total: number
  next: PathNext | null
}

export type PathProgress = {
  /** True when `next` was chosen by leverage (Pro) rather than path order. */
  personalised: boolean
  /** Every step, in path order (Math track then RW track). */
  steps: PathStepProgress[]
  tracks: PathTrackProgress[]
  /** The single "Continue" target across both tracks, or null when the path is complete. */
  next: PathNext | null
}

export type PathProgressInput = {
  evidence: TopicEvidence[]
  readSlugs: Iterable<string>
  /** The student's placement checks (placement-store.ts). Optional: none means none taken. */
  placements?: readonly PlacementRecord[]
  /** Rank the next step by leverage (Pro). Falls back to path order without evidence. */
  personalised: boolean
}

export const PATH_STATE_LABEL: Record<PathStepState, string> = {
  locked: 'Locked',
  available: 'Available',
  in_progress: 'In progress',
  passed: 'Passed',
}

/** The accuracy a gate demands, for copy like "score 70% or better". 0 for non-medal tiers. */
export function gateThresholdPct(gate: MasteryTier): number {
  return gate === 'bronze' || gate === 'silver' || gate === 'gold' ? MASTERY_THRESHOLDS[gate] : 0
}

/* ------------------------------------------------------------------ */
/*  Compute                                                            */
/* ------------------------------------------------------------------ */

export function computePathProgress(input: PathProgressInput): PathProgress {
  const evidence = new Map<string, { correct: number; total: number }>()
  for (const e of input.evidence) {
    const acc = evidence.get(e.topic) ?? { correct: 0, total: 0 }
    acc.correct += e.correct
    acc.total += e.total
    evidence.set(e.topic, acc)
  }
  const read = new Set(input.readSlugs)
  const steps = pathSteps()

  // Pass 1 — facts that depend only on evidence, so the prerequisite graph can
  // be evaluated against a complete "passed" set in pass 2.
  const facts = new Map<
    string,
    {
      tier: MasteryTier
      attempted: number
      pctExact: number
      passed: boolean
      passedBy: PathPassEvidence | null
      placement: StepPlacement
      read: boolean
    }
  >()
  for (const step of steps) {
    const e = evidence.get(step.topic)
    const attempted = e?.total ?? 0
    // Exact accuracy gates the tier so 69.6% does not round up into Silver;
    // the displayed number is still rounded.
    const pctExact = attempted > 0 ? (e!.correct / attempted) * 100 : 0
    const tier = masteryTier(pctExact, attempted)
    const byMastery = meetsGate(tier, step.gate)
    const placement = stepPlacement({
      records: (input.placements ?? []).filter((r) => r.topic === step.topic),
      passedByMastery: byMastery,
      attempted,
      checkpointCount: step.checkpoint.count,
    })
    // A placement pass stands only while mastery has no verdict of its own:
    // from a skill check's worth of answers on, the larger sample decides.
    const byPlacement = !byMastery && placement.passed !== null && attempted < step.checkpoint.count
    facts.set(step.topic, {
      tier,
      attempted,
      pctExact,
      passed: byMastery || byPlacement,
      passedBy: byMastery ? 'mastery' : byPlacement ? 'placement' : null,
      placement,
      read: read.has(step.lessonSlug) || step.modules.some((m) => read.has(m)),
    })
  }

  // Pass 2 — states.
  const progress: PathStepProgress[] = steps.map((step) => {
    const f = facts.get(step.topic)!
    const missing = step.prerequisites.filter((p) => !facts.get(p)?.passed)
    const state: PathStepState = f.passed
      ? 'passed'
      : f.attempted > 0 || f.read
        ? 'in_progress'
        : missing.length > 0
          ? 'locked'
          : 'available'
    return {
      topic: step.topic,
      lessonSlug: step.lessonSlug,
      section: step.section,
      state,
      tier: f.tier,
      accuracyPct: f.attempted > 0 ? Math.round(f.pctExact) : null,
      attempted: f.attempted,
      read: f.read,
      missing,
      passedBy: f.passedBy,
      placement: f.placement,
    }
  })

  const candidates = progress.filter((p) => p.state === 'available' || p.state === 'in_progress')
  const next = pickNext(candidates, input.personalised)

  const tracks: PathTrackProgress[] = LEARNING_PATH.map((track) => {
    const own = progress.filter((p) => p.section === track.section)
    return {
      section: track.section,
      title: track.title,
      passed: own.filter((p) => p.state === 'passed').length,
      total: own.length,
      next: pickNext(
        candidates.filter((p) => p.section === track.section),
        input.personalised,
      ),
    }
  })

  return {
    personalised: next?.reason === 'leverage',
    steps: progress,
    tracks,
    next,
  }
}

/**
 * The step to continue with, from the unlocked + unpassed candidates in path
 * order. Leverage first when personalised and there is evidence to rank on;
 * otherwise the step already underway, otherwise the first open one.
 */
function pickNext(candidates: PathStepProgress[], personalised: boolean): PathNext | null {
  if (candidates.length === 0) return null

  if (personalised) {
    const weak: WeakTopic[] = candidates.map((c) => ({
      topic: c.topic,
      section: c.section,
      pct: c.accuracyPct ?? 0,
      total: c.attempted,
    }))
    // rankTopicsByLeverage scores an unsampled topic 0 (no diagnosis without
    // evidence), so `leverage > 0` means "measured, below the ceiling".
    const best = rankTopicsByLeverage(weak).find((b) => b.leverage > 0)
    if (best) {
      const c = candidates.find((x) => x.topic === best.topic.topic)!
      return { topic: c.topic, lessonSlug: c.lessonSlug, reason: 'leverage', read: c.read }
    }
  }

  const underway = candidates.find((c) => c.state === 'in_progress')
  if (underway) {
    return {
      topic: underway.topic,
      lessonSlug: underway.lessonSlug,
      reason: 'in_progress',
      read: underway.read,
    }
  }
  const open = candidates.find((c) => c.state === 'available')
  return open
    ? { topic: open.topic, lessonSlug: open.lessonSlug, reason: 'first', read: open.read }
    : null
}
